import type { TokenGroup } from "../api/types/token.js";
import { flattenTokensByType } from "./tokens.js";

export interface ParsedDimension {
  value: number;
  unit: string;
}

export interface DimensionTokenMatch {
  name: string;
  value: string;
}

/** Parse a dimension string (e.g., "16px", "1.5rem") into value and unit. */
export function parseDimension(value: string): ParsedDimension | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  // Match number (int or float, optional negative) followed by unit
  const match = trimmed.match(
    /^(-?\d+(?:\.\d+)?)(px|rem|em|%|vw|vh|vmin|vmax|ch|ex|pt|pc|in|cm|mm)$/i,
  );

  if (!match) return null;

  return {
    value: parseFloat(match[1]),
    unit: match[2].toLowerCase(),
  };
}

const PROPERTY_TO_CATEGORY: Record<string, string> = {
  // Spacing properties
  padding: "spacing",
  "padding-top": "spacing",
  "padding-right": "spacing",
  "padding-bottom": "spacing",
  "padding-left": "spacing",
  margin: "spacing",
  "margin-top": "spacing",
  "margin-right": "spacing",
  "margin-bottom": "spacing",
  "margin-left": "spacing",
  gap: "spacing",
  "row-gap": "spacing",
  "column-gap": "spacing",
  // Border radius properties
  "border-radius": "borderRadius",
  "border-top-left-radius": "borderRadius",
  "border-top-right-radius": "borderRadius",
  "border-bottom-left-radius": "borderRadius",
  "border-bottom-right-radius": "borderRadius",
};

/**
 * Find the closest dimension token by unit and numeric value.
 * When property is provided, prefers tokens in the semantic category (e.g., padding -> spacing).
 */
export function findClosestDimensionToken(
  dim: string,
  tokens: TokenGroup,
  property?: string,
): DimensionTokenMatch | null {
  const parsed = parseDimension(dim);
  if (!parsed) return null;

  const dimensionTokens = flattenTokensByType(tokens, "dimension");
  const preferredCategory = property ? PROPERTY_TO_CATEGORY[property] : null;

  let closest: DimensionTokenMatch | null = null;
  let closestDiff = Infinity;
  let foundInCategory = false;

  for (const [name, tokenValue] of dimensionTokens) {
    const tokenParsed = parseDimension(tokenValue);
    if (!tokenParsed) continue;

    // Only match same unit
    if (tokenParsed.unit !== parsed.unit) continue;

    const diff = Math.abs(tokenParsed.value - parsed.value);

    // If we have a preferred category, prioritize tokens in that category
    if (preferredCategory) {
      const isInCategory = name.startsWith(preferredCategory);

      // If we haven't found a token in the preferred category yet,
      // accept any match from that category
      if (!foundInCategory && isInCategory) {
        closest = { name, value: tokenValue };
        closestDiff = diff;
        foundInCategory = true;
      }
      // If we're in the preferred category, keep the closest match
      else if (foundInCategory && isInCategory && diff < closestDiff) {
        closestDiff = diff;
        closest = { name, value: tokenValue };
      }
      // Skip tokens outside the preferred category if we've found one inside
      else if (foundInCategory && !isInCategory) {
        continue;
      }
    }

    // No property context or not in category yet - accept any closest match
    if (!foundInCategory && diff < closestDiff) {
      closestDiff = diff;
      closest = { name, value: tokenValue };
    }
  }

  return closest;
}
